import { useCallback, useContext, useEffect, useRef } from "react";

import { DIALOG } from "@/components/app/dialogs/dialog-provider";
import { useLocalEntryDialog } from "@/components/app/dialogs/use-local-entry-dialog";
import { EditorActionAudio } from "@/components/app/editor-v3/actions/editor-action-audio";
import { EditorActionEmbed } from "@/components/app/editor-v3/actions/editor-action-embed";
import { EditorActionEmoji } from "@/components/app/editor-v3/actions/editor-action-emoji";
import { EditorActionExpand } from "@/components/app/editor-v3/actions/editor-action-expand";
import { EditorActionFormatting } from "@/components/app/editor-v3/actions/editor-action-formatting";
import { EditorActionGif } from "@/components/app/editor-v3/actions/editor-action-gif";
import { EditorActionPhoto } from "@/components/app/editor-v3/actions/editor-action-photo";
import { EditorActionSubmit } from "@/components/app/editor-v3/actions/editor-action-submit";
import { EditorActionVideo } from "@/components/app/editor-v3/actions/editor-action-video";
import {
  EDITOR_MEDIA_TAB,
  EDITOR_MODE,
} from "@/components/app/editor-v3/constants";
import {
  EditorFooterActionButton,
  EditorFooterActionContainer,
} from "@/components/app/editor-v3/editor-action-utils";
import {
  EditorFieldContextProvider,
  useEditorContext,
} from "@/components/app/editor-v3/editor-context";
import { focusEditorEditableElement } from "@/components/app/editor-v3/editor-dom-focus";
import { EditorEmbedActionDetails } from "@/components/app/editor-v3/editor-embed-action-details";
import { EditorEmojiPicker } from "@/components/app/editor-v3/editor-emoji-picker";
import { EditorFileDropArea } from "@/components/app/editor-v3/editor-file-drop-area";
import { EditorMediaContainer } from "@/components/app/editor-v3/editor-media-container";
import { CommentOverlayEditor } from "@/components/app/editor-v3/editors/comment-overlay-editor";
import { TheEditor } from "@/components/app/editor-v3/the-editor";
import { EditorToolbar } from "@/components/app/editor-v3/toolbar";
import DesoAvatar from "@/components/shared/deso-avatar";
import { Badge } from "@/components/ui/badge";
import { useConfirm } from "@/components/ui/confirm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserContext } from "@/contexts/user-context";

export const CommentEditor = ({
  to,
  durationClassName = "duration-500",
  submitTitle = "",
  caption,
}: {
  to: string;
  durationClassName?: string;
  submitTitle?: string;
  caption?: string;
}) => {
  const {
    mode,
    activeMediaTab,
    submitting,
    empty,
    exposed,
    getDataAttributes,
  } = useEditorContext();
  const { user } = useContext(UserContext);
  const { confirm } = useConfirm();
  const overlayEditorRootRef = useRef<HTMLDivElement | null>(null);
  const confirmClose = useCallback(async () => {
    if (empty()) {
      return true;
    }

    return Boolean(
      await confirm({
        description:
          "You have unsaved content. Are you sure you want to discard?",
        type: "warning",
      }),
    );
  }, [confirm, empty]);
  const { open: modalOpen, setOpen: setModalOpen } = useLocalEntryDialog(
    DIALOG.CommentOverlayEditor,
    {
      beforeClose: confirmClose,
    },
  );

  exposed.modalOpen = modalOpen;
  exposed.setModalOpen = setModalOpen;

  const focusModalEditor = useCallback(() => {
    const overlayRoot = overlayEditorRootRef.current;

    if (!overlayRoot) {
      return false;
    }

    return focusEditorEditableElement(overlayRoot);
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    focusModalEditor();

    const frame = requestAnimationFrame(focusModalEditor);
    const timeout = window.setTimeout(focusModalEditor, 100);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [focusModalEditor, modalOpen]);

  return (
    <div className="relative flex w-full cursor-text flex-col">
      {submitting && (
        <div className="absolute inset-0 z-10 cursor-default bg-background/50" />
      )}
      {caption && <p className="mb-2 text-sm text-muted">{caption}</p>}
      <EditorFieldContextProvider name="body" placeholder={`Reply to ${to}...`}>
        <div className="relative flex">
          <div className="grow">
            <div className="flex grow">
              <div
                className={`
                  hidden w-auto overflow-hidden opacity-100
                  transition-[opacity,width] will-change-auto
                  in-data-[empty=false]:w-0 in-data-[empty=false]:opacity-0
                  in-data-[gated=true]:w-0 in-data-[gated=true]:opacity-0
                  xs:block
                  ${durationClassName}
                `}
              >
                <DesoAvatar
                  size={32}
                  account={user}
                  className="mr-3"
                  clickable={false}
                />
              </div>
              <div className="relative grow">
                <div
                  className={`
                    h-0 w-full overflow-hidden opacity-0
                    transition-[opacity,height] will-change-auto
                    in-data-[empty=false]:h-auto
                    in-data-[empty=false]:opacity-100
                    in-data-[gated=true]:h-auto in-data-[gated=true]:opacity-100
                    in-data-[mode=simple]:h-0
                    ${durationClassName}
                  `}
                >
                  <EditorToolbar />
                </div>
                <TheEditor />
              </div>
            </div>
          </div>

          <div
            className={`
              w-auto place-content-center overflow-hidden pl-2 opacity-100
              transition-[opacity,width] will-change-auto
              in-data-[empty=false]:w-0 in-data-[empty=false]:opacity-0
              in-data-[gated=true]:w-0 in-data-[gated=true]:opacity-0
              ${durationClassName}
            `}
          >
            <div className="flex gap-2">
              <EditorFieldContextProvider name="media">
                <EditorActionPhoto variant="ghost" className="h-auto p-1" />
              </EditorFieldContextProvider>
              <EditorFieldContextProvider name="media">
                <EditorActionVideo variant="ghost" className="h-auto p-1" />
              </EditorFieldContextProvider>
              <EditorFieldContextProvider name="media">
                <EditorActionAudio
                  variant="ghost"
                  className="h-auto p-1"
                  side="bottom"
                  align="end"
                />
              </EditorFieldContextProvider>
              <EditorFieldContextProvider name="body">
                <EditorActionGif
                  variant="ghost"
                  className="
                    hidden h-auto p-1
                    sm:inline-flex
                  "
                />
              </EditorFieldContextProvider>
            </div>
          </div>
        </div>
        <EditorFieldContextProvider name="media">
          <EditorMediaContainer />
          <EditorFileDropArea targetLabel="this reply" />
        </EditorFieldContextProvider>
      </EditorFieldContextProvider>

      <div
        className={`
          h-0 overflow-hidden transition-[height] will-change-auto
          in-data-[media-tab=emoji]:h-auto
          in-data-[media-tab=poll]:opacity-100
          ${durationClassName}
        `}
      >
        <EditorFieldContextProvider name="body">
          <div className="mt-4" />
          <EditorEmojiPicker />
        </EditorFieldContextProvider>
      </div>

      <div
        className={`
          h-0 overflow-hidden transition-[height] will-change-auto
          in-data-[media-tab=embed]:h-auto in-data-[media-tab=embed]:opacity-100
          ${durationClassName}
        `}
      >
        <div className="my-4 h-px bg-border" />
        <Badge variant="outline" className="mb-4 inline-flex">
          Embed content
        </Badge>
        <EditorEmbedActionDetails />
      </div>

      <div
        className={`
          h-0 overflow-hidden opacity-0 transition-[opacity,height]
          will-change-auto
          in-data-[empty=false]:h-auto in-data-[empty=false]:opacity-100
          in-data-[gated=true]:h-auto in-data-[gated=true]:opacity-100
          ${durationClassName}
        `}
      >
        <div className="mt-4 flex">
          <div className="grow">
            <EditorFooterActionContainer>
              <EditorFooterActionButton
                action={EditorActionFormatting}
                tooltipText="Formatting"
                active={mode === EDITOR_MODE.RICH}
              />
              <EditorFieldContextProvider name="media">
                <EditorFooterActionButton
                  action={EditorActionPhoto}
                  tooltipText="Upload Images"
                />
              </EditorFieldContextProvider>
              <EditorFieldContextProvider name="media">
                <EditorFooterActionButton
                  action={EditorActionVideo}
                  tooltipText="Upload Videos"
                />
              </EditorFieldContextProvider>
              <EditorFieldContextProvider name="media">
                <EditorFooterActionButton
                  action={EditorActionAudio}
                  tooltipText="Upload Audio"
                  side="top"
                />
              </EditorFieldContextProvider>
              <EditorFieldContextProvider name="body">
                <EditorFooterActionButton
                  action={EditorActionGif}
                  tooltipText="Add GIF"
                />
              </EditorFieldContextProvider>
              <EditorFooterActionButton
                action={EditorActionEmbed}
                tooltipText="Manage embedded media"
                active={activeMediaTab === EDITOR_MEDIA_TAB.Embed}
              />
              <EditorFieldContextProvider name="body">
                <EditorFooterActionButton
                  action={EditorActionEmoji}
                  tooltipText="Add emoji"
                  active={activeMediaTab === EDITOR_MEDIA_TAB.Emoji}
                  className="
                    hidden
                    md:block
                  "
                />
              </EditorFieldContextProvider>
            </EditorFooterActionContainer>
          </div>

          <div className="ml-auto flex gap-2">
            <div>
              <Dialog
                open={modalOpen}
                onOpenChange={async (nextOpen) => {
                  if (nextOpen) {
                    setModalOpen(true);
                    return;
                  }

                  if (!(await confirmClose())) {
                    return false;
                  }

                  setModalOpen(false);
                }}
              >
                <EditorFooterActionButton
                  action={EditorActionExpand}
                  tooltipText="Expand editor"
                  onClick={() => setModalOpen(true)}
                />
                <DialogContent
                  bodyClassName="
                    unforce-radius rounded-none border-0 shadow-none
                  "
                  className="
                    content
                    h-(--overlay-keyboard-available-height) max-h-(--overlay-keyboard-available-height) max-w-full
                    overflow-hidden rounded-none border-0
                    md:max-h-[90vh] md:max-w-[700px] md:rounded-lg
                  "
                  closeClassName="top-2 right-2 size-9 p-2"
                  onOpenAutoFocus={(event) => {
                    event.preventDefault();
                  }}
                  hideOverlay
                  {...getDataAttributes()}
                >
                  <div ref={overlayEditorRootRef} className="contents">
                    <style>{`html.os-ios body > main { opacity:0 }`}</style>
                    <DialogTitle className="hidden">Add a post</DialogTitle>
                    <DialogDescription className="hidden">
                      Add Post
                    </DialogDescription>
                    <CommentOverlayEditor to={to} submitTitle={submitTitle} />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div>
              <EditorActionSubmit className="px-3" title={submitTitle} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
